import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Authenticated from '@/Layouts/Authenticated';
import { Table, Alert, Modal, Form, FormControl, Button } from 'react-bootstrap-v5'

export default function Users({ app, auth, roles, plans }) {
    const [users, setUsers] = useState(null)
    const [search, setSearch] = useState(null)
    const [message, setMessage] = useState(null)
    const [loading, setLoading] = useState(false)

    const [editUser, setEditUser] = useState(null)
    const [editing, setEditing] = useState(false)

    const userRole = (user) => user.customClaims && user.customClaims.role
    const userProviders = (user) => user.providerData.map((item) => item.providerId.split('.')[0])

    const handleEditUser = (user) => {
        setEditUser({
            uid: user.uid,
            email: user.email,
            role: userRole(user) ?? '',
            subscription: user.subscription ?? {},
        })
    }

    const handleClose = () => setEditUser(null)

    const handleChange = (event) => {
        const { dataset, name, value } = event.target;
        const updated = {
            ...editUser,
            ...(dataset.nested ? {
                [dataset.nested]: {
                    ...editUser[dataset.nested],
                    [name]: value
                }
            } : {
                [name]: value
            })
        }
        setEditUser(updated)
    }

    const handleSubmit = async () => {
        await updateUser()
        handleClose()
    }

    const updateUser = async () => {
        setEditing(true)
        const res = await axios.put(route('users.update'), editUser)
        const updated = res.data
        setEditing(false)

        const newList = users.map((item) => {
            return item.uid == editUser.uid ? updated : item
        });
        setUsers(newList)
    }

    useEffect(async () => {
        if (!search) {
            return
        }

        setLoading(true)
        setMessage(null)
        setUsers(null)

        const url = search ? route('users.search', { search: search }) : route('users.users');
        const res = await axios.get(url)
        res.data.error ? setMessage(res.data.error) : setUsers(res.data)
        setLoading(false)
    }, [search])

    return (
        <Authenticated app={app} auth={auth}>
            <div className="category-title">
                <div className="container">
                    <h1>Manage Users</h1>
                </div>
            </div>

            <div className="content-wrapper">
                <div className="container">
                    <FormControl type="text" className="mb-3" size="lg" defaultValue={search} placeholder="Search by email address or user UID"
                        onBlur={event => setSearch(event.target.value)}
                        onKeyPress={event => event.key === 'Enter' && event.target.blur()}
                    />

                    {message && (
                        <Alert variant="info">
                            {message}
                        </Alert>
                    )}

                    {loading && (
                        <Alert variant="info">
                            Loading...
                        </Alert>
                    )}

                    {users && (
                        <Table striped responsive>
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Provider</th>
                                    <th>Photo</th>
                                    <th>Role</th>
                                    <th>Created</th>
                                    <th>SignedIn</th>
                                    <th>Subscription</th>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {users && (
                                    users.map(user => (
                                        <tr key={user.uid}>
                                            <td>
                                                <p>{user.displayName}</p>
                                                <p>{user.email}</p>
                                                <p>{user.uid}</p>
                                            </td>
                                            <td>
                                                {userProviders(user).map((item) => (
                                                    <div key={item}>{item}</div>
                                                ))}
                                            </td>
                                            <td>
                                                {user.photoUrl && user.photoUrl.startsWith('http') && (
                                                    <img src={user.photoUrl} alt="photo" style={{ width: '2rem' }}></img>
                                                )}
                                            </td>
                                            <td>{userRole(user)}</td>
                                            <td>{user.metadata.createdAt}</td>
                                            <td>{user.metadata.lastLoginAt}</td>
                                            <td>
                                                {user.subscription && (
                                                    <>
                                                        {user.subscription.subsciber_expiry_date && (
                                                            <p>ExpiryDate: {user.subscription.subsciber_expiry_date}</p>
                                                        )}
                                                        {user.subscription.subsciber_updated_by && (
                                                            <p>UpdatedBy: {user.subscription.subsciber_updated_by}</p>
                                                        )}
                                                        {user.subscription.subsciber_update_time && (
                                                            <p>UpdatedAt: {user.subscription.subsciber_update_time}</p>
                                                        )}
                                                    </>
                                                )}
                                            </td>
                                            <td>
                                                <Button onClick={() => handleEditUser(user)}>Edit</Button>
                                            </td>
                                            <td>
                                                <pre className="d-none">
                                                    {JSON.stringify(user, null, 2)}
                                                </pre>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    )}
                </div>
            </div>

            {editUser && (
                <Modal show={editUser != null} onHide={handleClose} style={{ top: '80px' }}>
                    <Modal.Header closeButton>
                        <Modal.Title>{editUser.email}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form>
                            <Form.Group className="mb-3" controlId="role">
                                <Form.Label>Role</Form.Label>
                                {editUser.uid == auth.user.uid ? (
                                    <Form.Control type="text" value={editUser.role} readOnly plaintext />
                                ) : (
                                    <Form.Control as="select" name="role" value={editUser.role} onChange={handleChange}>
                                        <option value="">-- n/a --</option>
                                        {roles.map((role) => (
                                            <option key={role} value={role}>{role}</option>
                                        ))}
                                    </Form.Control>
                                )}
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="subsciber_expiry_date">
                                <Form.Label>Subscription Expiry Date</Form.Label>
                                <Form.Control type="date"
                                    data-nested="subscription"
                                    name="subsciber_expiry_date"
                                    value={editUser.subscription.subsciber_expiry_date ?? ''}
                                    onChange={handleChange} />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="subsciber_subscription_id">
                                <Form.Label>Subscription ID</Form.Label>
                                <Form.Control type="text"
                                    data-nested="subscription"
                                    name="subsciber_subscription_id"
                                    value={editUser.subscription.subsciber_subscription_id ?? ''}
                                    onChange={handleChange} />
                            </Form.Group>
                            <Form.Group className="mb-3" controlId="subsciber_plan_id">
                                <Form.Label>Subscription Plan</Form.Label>
                                <Form.Control as="select"
                                    data-nested="subscription"
                                    name="subsciber_plan_id"
                                    value={editUser.subscription.subsciber_plan_id ?? ''}
                                    onChange={handleChange}
                                >
                                    <option value="">-- n/a --</option>
                                    {plans.map((plan) => (
                                        <option key={plan.id} value={plan.id}>{plan.id} - {plan.description}</option>
                                    ))}
                                </Form.Control>
                            </Form.Group>
                        </Form>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>Close</Button>
                        <Button variant="primary" onClick={handleSubmit} disabled={editing}>Save Changes</Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Authenticated>
    );
}
